import { useState, ReactNode, useEffect, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Image as ContentIcon,
  PlaylistPlay as PlaylistIcon,
  ViewQuilt as LayoutIcon,
  Tv as DisplayIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  People as UsersIcon,
  Business as WorkspacesIcon,
  Email as EmailIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../contexts/AuthContext';
import { useGetWorkspaceByIdQuery } from '../../store/api/workspaceApi';
import { setCurrentWorkspace } from '../../store/slices/authSlice';
import LanguageSwitcher from '../LanguageSwitcher';
import SkipToContent from '../SkipToContent';

const DRAWER_WIDTH = 260;
const MOBILE_DRAWER_WIDTH = 280;

interface MainLayoutProps {
  children: ReactNode;
}

interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  path: string;
  roles?: string[]; // If specified, only show for these roles
}

/**
 * Main application layout component
 * Provides top navigation bar, sidebar menu, and main content area
 * Responsive design for mobile devices
 * Requirements: All (UI structure), 1.4, 1.5, 1.6 (role-based menu visibility)
 */
const MainLayout = ({ children }: MainLayoutProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, currentWorkspace, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Fetch workspace details to check account type and get logo
  const { data: workspaceData } = useGetWorkspaceByIdQuery(currentWorkspace?.id || '', {
    skip: !currentWorkspace?.id,
  });

  // Update currentWorkspace in auth state when workspace data changes (including logo)
  useEffect(() => {
    if (workspaceData && currentWorkspace) {
      // Only update if something actually changed
      if (
        currentWorkspace.logoUrl !== workspaceData.logoUrl ||
        currentWorkspace.brandColor !== workspaceData.brandColor ||
        currentWorkspace.accentColor !== workspaceData.accentColor ||
        currentWorkspace.name !== workspaceData.name
      ) {
        const updatedWorkspace = {
          ...currentWorkspace,
          logoUrl: workspaceData.logoUrl,
          brandColor: workspaceData.brandColor,
          accentColor: workspaceData.accentColor,
          name: workspaceData.name,
        };
        dispatch(setCurrentWorkspace(updatedWorkspace));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceData?.logoUrl, workspaceData?.brandColor, workspaceData?.accentColor, workspaceData?.name, dispatch]);

  // Check if this is a company account (single-user workspace)
  // Company accounts have metadata.account_type = 'company' OR user role is 'manager'
  const isCompanyAccount = 
    workspaceData?.metadata?.account_type === 'company' || 
    currentWorkspace?.role === 'manager';

  // Get current language
  const currentLang = i18n.language || 'uz';

  // Helper function to convert S3 URLs to proxied URLs
  const getProxiedMediaUrl = useMemo(() => (url: string | null): string | null => {
    if (!url) return null;
    
    // If it's already pointing to our media proxy, return as is
    if (url.includes('/api/media/')) return url;
    
    // If it's an S3 URL, extract the key and proxy it
    if (url.includes('amazonaws.com') || url.includes('s3.')) {
      const urlParts = url.split('/');
      const keyIndex = urlParts.findIndex(part => part.includes('lino-signage'));
      if (keyIndex !== -1 && keyIndex < urlParts.length - 1) {
        const key = urlParts.slice(keyIndex + 1).join('/');
        return `http://localhost:3000/api/media/${key}`;
      }
    }
    
    // If it's just a key, proxy it
    if (!url.startsWith('http')) {
      return `http://localhost:3000/api/media/${url}`;
    }
    
    return url;
  }, []);

  // Navigation menu items with role-based visibility
  const navItems: NavItem[] = useMemo(() => [
    {
      key: 'dashboard',
      label: t('navigation.dashboard'),
      icon: <DashboardIcon />,
      path: `/${currentLang}/dashboard`,
    },
    {
      key: 'content',
      label: t('navigation.content'),
      icon: <ContentIcon />,
      path: `/${currentLang}/content`,
      roles: ['admin', 'manager'], // Viewers have read-only access
    },
    {
      key: 'playlists',
      label: t('navigation.playlists'),
      icon: <PlaylistIcon />,
      path: `/${currentLang}/playlists`,
      roles: ['admin', 'manager'],
    },
    {
      key: 'layouts',
      label: t('navigation.layouts'),
      icon: <LayoutIcon />,
      path: `/${currentLang}/layouts`,
      roles: ['admin', 'manager'],
    },
    {
      key: 'displays',
      label: t('navigation.displays'),
      icon: <DisplayIcon />,
      path: `/${currentLang}/displays`,
      roles: ['admin', 'manager'],
    },
    // Schedules hidden - using instant push via MQTT instead
    // {
    //   key: 'schedules',
    //   label: t('navigation.schedules'),
    //   icon: <ScheduleIcon />,
    //   path: `/${currentLang}/schedules`,
    //   roles: ['admin', 'manager'],
    // },
    {
      key: 'analytics',
      label: t('navigation.analytics'),
      icon: <AnalyticsIcon />,
      path: `/${currentLang}/analytics`,
    },
    {
      key: 'users',
      label: t('navigation.users'),
      icon: <UsersIcon />,
      path: `/${currentLang}/users`,
      roles: ['admin'], // Admin only
    },
    {
      key: 'emails',
      label: 'Email Logs',
      icon: <EmailIcon />,
      path: `/${currentLang}/emails`,
      roles: ['superadmin'], // Superadmin only
    },
    {
      key: 'workspaces',
      label: t('navigation.workspaces'),
      icon: <WorkspacesIcon />,
      path: `/${currentLang}/workspaces`,
      roles: ['admin'], // Admin only
    },
    {
      key: 'settings',
      label: t('navigation.settings'),
      icon: <SettingsIcon />,
      path: `/${currentLang}/settings`,
    },
  ], [currentLang, t]);

  // Filter menu items based on user role
  const visibleNavItems = useMemo(() => navItems.filter((item) => {
    // Hide Users tab for company accounts
    if (item.key === 'users' && isCompanyAccount) {
      return false;
    }
    
    // Hide Workspaces tab for company accounts
    if (item.key === 'workspaces' && isCompanyAccount) {
      return false;
    }
    
    // Handle superadmin-only items
    if (item.roles?.includes('superadmin')) {
      return user?.isSuperadmin === true;
    }
    
    if (!item.roles) return true; // No role restriction
    return item.roles.includes(currentWorkspace?.role || '');
  }), [navItems, isCompanyAccount, user?.isSuperadmin, currentWorkspace?.role]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const handleWorkspaceSwitch = () => {
    handleMenuClose();
    navigate('/workspace-selector');
  };

  // Sidebar drawer content
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 80,
        }}
      >
        {currentWorkspace?.logoUrl ? (
          <img
            src={getProxiedMediaUrl(currentWorkspace.logoUrl) || ''}
            alt={currentWorkspace.name}
            crossOrigin="anonymous"
            style={{
              maxWidth: '80%',
              maxHeight: 60,
              objectFit: 'contain',
            }}
          />
        ) : (
          <img
            src="/lino.svg"
            alt="Lino"
            style={{
              width: 120,
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* Workspace info */}
      {currentWorkspace && (
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('workspace.select_workspace')}
          </Typography>
          <Typography variant="body1" fontWeight={600} noWrap>
            {currentWorkspace.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(`workspace.role_${currentWorkspace.role}`)}
          </Typography>
        </Box>
      )}

      {/* Navigation menu */}
      <List sx={{ flex: 1, py: 1 }} role="navigation" aria-label={t('navigation.main_menu') || 'Main menu'}>
        {visibleNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavigation(item.path)}
                role="link"
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 1,
                  minHeight: { xs: 48, md: 44 }, // Touch-friendly height on mobile
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.contrastText,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.secondary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Skip to content link for keyboard navigation */}
      <SkipToContent />
      
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          zIndex: theme.zIndex.drawer - 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
            aria-label={t('navigation.toggle_menu') || 'Toggle navigation menu'}
          >
            <MenuIcon />
          </IconButton>

          {/* Page title - hidden on mobile */}
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }}
          >
            {visibleNavItems.find((item) => item.path === location.pathname)
              ?.label || t('navigation.dashboard')}
          </Typography>

          {/* Mobile logo */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              display: { xs: 'block', md: 'none' },
              fontWeight: 700,
              color: theme.palette.primary.main,
            }}
          >
            Lino
          </Typography>

          {/* Language switcher */}
          <Box sx={{ mr: 2 }}>
            <LanguageSwitcher />
          </Box>

          {/* User menu */}
          <IconButton 
            onClick={handleMenuOpen} 
            size="large"
            aria-label={t('navigation.user_menu') || 'User menu'}
            aria-controls={Boolean(anchorEl) ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
              }}
            >
              {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            MenuListProps={{
              'aria-labelledby': 'user-menu-button',
              role: 'menu',
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => handleNavigation(`/${currentLang}/settings`)} role="menuitem">
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('settings.profile')}</ListItemText>
            </MenuItem>
            {!isCompanyAccount && (
              <MenuItem onClick={handleWorkspaceSwitch} role="menuitem">
                <ListItemIcon>
                  <WorkspacesIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('workspace.select_workspace')}</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout} role="menuitem">
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('auth.logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Navigation */}
      <Box
        component="nav"
        sx={{
          width: { md: DRAWER_WIDTH },
          flexShrink: { md: 0 },
        }}
        aria-label={t('navigation.main_navigation') || 'Main navigation'}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: MOBILE_DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        {/* Toolbar spacer */}
        <Toolbar />

        {/* Page content */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
